import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Alert, Divider, Typography } from "@mui/material";
import PropTypes from "prop-types";

// Layout Components
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import DashboardNavbar from "common/Navbars/DashboardNavbar";
import Footer from "common/Footer";
import SuiBox from "components/SuiBox";

// Stepper Components
import StepperNavigation from "./components/StepperNavigation";
import StepperContent from "./components/StepperContent";
import StepperActions from "./components/StepperActions";

import SuiTypography from "components/SuiTypography";

// API
import SedesApi from "api/sedesApi";
import ProcesosApi from "api/procesosApi";
import { obtenerMensajeError } from "utils/apiErrors";
import DatosBasicosApi from "api/datosbasicosApi";

// Styles
import useStyles from "./styles";

const steps = [
  "1. Deudor y Solicitud",
  "2. Acreencias",
  "3. Bienes",
  "4. Adicionales",
  "5. Finanzas y Propuesta",
  "6. Docs. Anexos",
  "7. Validaciones",
];

function RegistrarProceso(props) {
  const { location = { pathname: "", state: {} } } = props;
  //const { id } = useParams();
  const { identificador } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const finanzasFormRef = useRef(null);
  const verificacionRef = useRef(null); // Añadimos esta línea

  // Estados
  const [activeStep, setActiveStep] = useState(0);
  //const [nextStep, setNextStep] = useState(null);
  const [skipped, setSkipped] = useState(new Set());
  const [disableNext, setDisableNext] = useState(false);
  const [error, setError] = useState(undefined);
  const [procesoId, setProcesoId] = useState(null);
  const [procesoCreado, setProcesoCreado] = useState(!!identificador);
  const [procesoIdentificador, setProcesoIdentificador] = useState(identificador || null);
  const [isEditing] = useState(!!identificador);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    clienteIdentificacion: "",
    clienteTipoIdentificacion: "",
    clienteNombres: "",
    clienteApellidos: "",
    clienteGenero: "",
    clienteFechaNacimiento: null,
    clienteDireccion: "",
    clienteCelular: "",
    clienteEmail: "",
    clienteTipoTrabajador: "",
    clienteTipoPersona: "",
    clienteSector: "",
    tipoSeleccionado: "",
    descripcion: "",
    causas: "",
    poseeBienes: false,
    procesosJudiciales: false,
    personasACargo: false,
    sociedadesProceso: false,
    tiposProceso: [],
    sedeSeleccionada: "",
    sedes: [],
    finalidad: "",
    compromiso_mensual: "",
  });

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tiposResponse, sedesResponse] = await Promise.all([
        DatosBasicosApi.fetchTiposProcesoActivos(),
        SedesApi.fetchSedes(),
      ]);

      if (identificador) {
        const procResponse = await ProcesosApi.fetchProcesoByIdentificador(identificador);
        const proceso = procResponse.data;
        const clienteData = proceso.cliente_detalle || {};
        setFormData({
          clienteIdentificacion: clienteData.identificacion?.toString() || "",
          clienteTipoIdentificacion: clienteData.tipo_identificacion || "",
          clienteNombres: clienteData.nombres || "",
          clienteApellidos: clienteData.apellidos || "",
          clienteEmail: clienteData.email || "",
          clienteDireccion: clienteData.direccion || "",
          clienteCelular: clienteData.celular || "",
          clienteTipoTrabajador: clienteData.tipo_trabajador || "",
          clienteTipoPersona: clienteData.tipo_persona || "",
          clienteSector: clienteData.sector_trabajador || "",
          clienteGenero: clienteData.genero || "",
          clienteFechaNacimiento: clienteData.fecha_nacimiento || null,
          tipoSeleccionado: proceso.tipo_proceso || "",
          descripcion: proceso.descripcion || "",
          causas: proceso.causas || "",
          poseeBienes: proceso.posee_bienes || false,
          procesosJudiciales: proceso.procesos_judiciales || false,
          personasACargo: proceso.personas_a_cargo || false,
          sociedadesProceso: proceso.sociedades || false,
          sedeSeleccionada: proceso.sede || "",
          tiposProceso: tiposResponse.data || [],
          sedes: sedesResponse.data || [],
          finalidad: proceso.finalidad || "",
          compromiso_mensual: proceso.compromiso_mensual || "",
          sede_detalle: proceso.sede_detalle || null,
          tipo_proceso_detalle: proceso.tipo_proceso_detalle || null,
        });
        setProcesoId(proceso.id);
        setProcesoIdentificador(proceso.identificador);
      } else {
        setFormData((prev) => ({
          ...prev,
          tiposProceso: tiposResponse.data,
          sedes: sedesResponse.data,
        }));
      }
    } catch (error) {
      console.error("Error cargando los Datos Iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los Datos Iniciales"));
    } finally {
      setLoading(false);
    }
  }, [identificador, location]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const isStepOptional = useCallback(() => {
    return null;
  }, []);

  const isStepSkipped = useCallback(
    (step) => {
      return skipped.has(step);
    },
    [skipped]
  );

  const handleNext = async (targetStep = null) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(undefined);

    try {
      // Evitar manejar eventos accidentalmente
      if (targetStep instanceof Event) {
        console.warn("Se recibió un evento en lugar de un targetStep:", targetStep);
        return;
      }
      const nextStep = targetStep !== null ? targetStep : activeStep + 1;
      // Si estamos regresando a un paso anterior, no validamos
      if (targetStep !== null && targetStep < activeStep) {
        setDisableNext(false);
        setActiveStep(nextStep);
        setIsSubmitting(false);
        return;
      }
      // Paso 0: Validación del cliente
      if (activeStep === 0) {
        const response = await handleClienteSubmit();
        if (!response) {
          setIsSubmitting(false);
          return;
        }
        //alert("response.status: " + JSON.stringify(response.status));
        if ((!isEditing || !procesoCreado) && response.status === 201) {
          const newProcesoId = response.data.id;
          setProcesoId(newProcesoId);
          setProcesoCreado(true);
          setProcesoIdentificador(response.data.identificador);
        }
      }
      // Validación para el paso de Bienes
      if (activeStep === 2 && procesoCreado) {
        const procesoData = {
          posee_bienes: formData.poseeBienes,
          procesos_judiciales: formData.procesosJudiciales,
          personas_a_cargo: formData.personasACargo,
          sociedades: formData.sociedadesProceso,
        };
        const response = await ProcesosApi.actualizarProceso(procesoId, procesoData);
        if (response.status !== 200) {
          setError("Error al actualizar la información de bienes");
          return;
        }
      }

      // Validación para el paso de Finanzas
      if (activeStep === 4 && procesoCreado) {
        if (!finanzasFormRef.current)
          throw new Error("No se encontró la referencia al formulario de Finanzas");
        // Verificar si existe una propuesta activa
        const propuestasResponse = await ProcesosApi.obtenerPropuestas(procesoId, "solicitud");
        const propuestaActiva = propuestasResponse.data[0];
        if (!propuestaActiva)
          throw new Error("Debe generar una proyección de pagos antes de continuar");
        const saved = await finanzasFormRef.current.saveFinanzas();
        if (!saved) {
          setIsSubmitting(false);
          return;
        }
        if (!formData.compromiso_mensual) {
          throw new Error("Debe establecer un compromiso mensual");
        }
      }

      // Validación para el paso de VALIDACIONES
      if (activeStep === 6 && procesoCreado && verificacionRef.current) {
        const success = await verificacionRef.current.handleFinish();
        if (!success) {
          setIsSubmitting(false);
          return;
        }
      }
      // Avanzar al paso siguiente
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      // Finalmente avanzar
      setActiveStep(nextStep);
      setSkipped(newSkipped);
      setDisableNext(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "Ocurrió un error al continuar");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleStepClick = async (step) => {
  //   if (step === activeStep) return; // Si el usuario hace clic en el paso actual, no hacemos nada.
  //   // Ir al paso seleccionado
  //   await handleNext(step);
  // };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setIsSubmitting(false);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("No puedes saltarte un paso que no sea opcional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    history.push("/procesos/consultar");
  };

  const handleClienteSubmit = async () => {
    const requiredFields = [
      { field: "clienteIdentificacion", message: "Debes ingresar la Identificación del Deudor." },
      {
        field: "clienteTipoIdentificacion",
        message: "Debes ingresar el Tipo de Identificación del Deudor.",
      },
      { field: "clienteNombres", message: "Debes ingresar el Nombre del Deudor." },
      { field: "clienteApellidos", message: "Debes ingresar el Apellido del Deudor." },
      { field: "clienteEmail", message: "Debes ingresar el Correo Electrónico del Deudor." },
      { field: "clienteCelular", message: "Debes ingresar el Celular del Deudor." },
      //{ field: "clienteDireccion", message: "Debes ingresar la Dirección del Cliente." },
      { field: "clienteGenero", message: "Debes ingresar el Género del Deudor." },
      {
        field: "clienteTipoTrabajador",
        message: "Debes ingresar el Tipo de Trabajador del Deudor.",
      },
      {
        field: "clienteTipoPersona",
        message: "Debes ingresar el Tipo de Persona del Deudor.",
      },
      { field: "clienteSector", message: "Debes ingresar el Sector del Deudor." },
      {
        field: "clienteFechaNacimiento",
        message: "Debes ingresar la Fecha de Nacimiento del Deudor.",
      },
      { field: "tipoSeleccionado", message: "Debes ingresar un Tipo de Solicitud." },
      { field: "sedeSeleccionada", message: "Debes seleccionar una Sede." },
      { field: "finalidad", message: "Debes seleccionar una Finalidad." },
    ];

    for (const { field, message } of requiredFields) {
      if (!formData[field]) {
        setError(message);
        setIsSubmitting(false);
        return null;
      }
    }

    try {
      const procesoData = {
        cliente_identificacion: formData.clienteIdentificacion,
        cliente_tipo_identificacion: formData.clienteTipoIdentificacion,
        cliente_nombres: formData.clienteNombres,
        cliente_apellidos: formData.clienteApellidos,
        cliente_email: formData.clienteEmail,
        cliente_direccion: formData.clienteDireccion,
        cliente_celular: formData.clienteCelular,
        cliente_tipotrabajador: formData.clienteTipoTrabajador,
        cliente_tipopersona: formData.clienteTipoPersona,
        cliente_sector: formData.clienteSector,
        cliente_genero: formData.clienteGenero,
        cliente_fecha_nacimiento: formData.clienteFechaNacimiento,
        finalidad: formData.finalidad,
        sede: formData.sedeSeleccionada,
        //cliente: formData.clienteIdentificacion,
        cliente: formData.clienteId,
        tipo_proceso: formData.tipoSeleccionado,
        descripcion: formData.descripcion,
        causas: formData.causas,
        posee_bienes: formData.poseeBienes,
        procesos_judiciales: formData.procesosJudiciales,
        personas_a_cargo: formData.personasACargo,
        sociedadesProceso: formData.sociedadesProceso,
      };
      if (isEditing || procesoCreado) {
        const response = await ProcesosApi.actualizarProceso(procesoId, procesoData);
        setError(undefined);
        return response;
      } else {
        // Verificar procesos existentes
        const procesosResponse = await ProcesosApi.fetchProcesosByIdentificacion(
          formData.clienteIdentificacion
        );

        if (procesosResponse.data?.length > 0) {
          const confirmar = window.confirm(
            `El cliente con identificación ${formData.clienteIdentificacion} ya tiene ${procesosResponse.data.length} proceso(s) existente(s).\n\n¿Desea crear una nueva solicitud para este cliente?`
          );
          if (!confirmar) {
            setIsSubmitting(false);
            return null;
          }
        }
        // Añadir campos adicionales para nueva proceso
        const newProcesoData = {
          ...procesoData,
          consecutivo: 0,
          identificador: "-",
          id_periodo: "year",
        };
        const response = await ProcesosApi.crearProceso(newProcesoData);
        if (response.status === 201) {
          setProcesoCreado(true);
          setError(undefined);
        }
        setIsSubmitting(false);
        return response;
      }
    } catch (err) {
      console.error("Error al procesar la Solicitud:", err);
      setError(obtenerMensajeError(err, "Ocurrió un error al procesar la Solicitud."));
      return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox display="flex" justifyContent="center" alignItems="center" py={6}>
          <Typography>Cargando...</Typography>
        </SuiBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox>
        <Box className={classes.stepperContainer} sx={{ mt: 0, pt: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: -5 }}>
            <Typography variant="h5">
              {isEditing || procesoCreado ? "Editar Solicitud" : "Nueva Solicitud"}
            </Typography>
            {procesoIdentificador && (
              <Typography
                variant="subtitle1"
                className={classes.procesoIdentificador}
                sx={{
                  backgroundColor: "primary.light",
                  color: "primary.contrastText",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  fontWeight: "medium",
                  position: "relative", // Aseguramos que no se mueva
                }}
              >
                {procesoIdentificador}
              </Typography>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          <StepperNavigation
            steps={steps}
            activeStep={activeStep}
            isStepOptional={isStepOptional}
            isStepSkipped={isStepSkipped}
            //onStepClick={handleStepClick}
            onStepClick={(index) => {
              setActiveStep(index);
              // ✅ solo algunos pasos necesitan control
              if ([0, 4].includes(index)) {
                // por ejemplo, paso 0 (Deudor - Solicitud) , 4 (proyección) lo controla StepperContent
                return;
              }
              // para otros pasos, lo habilitamos
              setDisableNext(false);
            }}
          />
          <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
          <SuiBox>
            {activeStep === steps.length ? (
              <React.Fragment>
                <SuiTypography variant="h4" fontWeight="medium">
                  Proceso {isEditing ? "actualizado" : "completado"} exitosamente.
                </SuiTypography>
                <SuiBox display="flex" justifyContent="flex-end">
                  <StepperActions
                    activeStep={activeStep}
                    stepsLength={steps.length}
                    isStepOptional={isStepOptional}
                    handleBack={handleBack}
                    handleSkip={handleSkip}
                    handleNext={handleNext}
                    handleReset={handleReset}
                    isComplete={true}
                    isEditing={isEditing}
                    disableNext={disableNext || isSubmitting}
                  />
                </SuiBox>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <StepperContent
                  activeStep={activeStep}
                  formData={formData}
                  setFormData={setFormData}
                  setError={setError}
                  procesoCreado={procesoCreado}
                  procesoId={procesoId}
                  isEditing={isEditing}
                  finanzasRef={finanzasFormRef}
                  verificacionRef={verificacionRef} // Añadimos esta línea
                  handleNext={handleNext}
                  setDisableNext={setDisableNext}
                  disableNext={disableNext}
                />

                <StepperActions
                  activeStep={activeStep}
                  stepsLength={steps.length}
                  isStepOptional={isStepOptional}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  handleNext={handleNext}
                  handleReset={handleReset}
                  isComplete={false}
                  isEditing={isEditing}
                  disableNext={disableNext || isSubmitting}
                />
              </React.Fragment>
            )}
          </SuiBox>
        </Box>
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

RegistrarProceso.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.object,
  }),
};

export default RegistrarProceso;
